import { useMutation, useQuery } from '@tanstack/react-query';
import type { APICommonResponse } from 'src/types/common.types';

import type { PackName } from '@/store/server/common/digital-card/_constant';
import type {
  BuyStickerResponse,
  DrawLuckyDrawResponse,
  FinishBlockMiniGameResponse,
  FinishMiniGameResponse,
  GetBlockMiniGameInitResponse,
  GetBlockMiniGameRankingResponse,
  GetLuckyDrawHistoryResponse,
  GetLuckyDrawTicketCountResponse,
  GetMatchEventHistoryResponse,
  GetMiniGameRankingResponse,
  GetMiniGameStatusResponse,
  GetPointSaveResponse,
  GetPointUseDetailResponse,
  GetPointUseResponse,
  GetRealTimeCardRequest,
  GetRealTimeCardResponse,
  GetRealTimeEditionResponse,
  GetRepresentCardResponse,
  GetRepresentGalleryResponse,
  GetStickerListResponse,
  GetTopCollectorPeriodRequest,
  GetTopCollectorPeriodResponse,
  GetTopCollectorRequest,
  GetTopCollectorResponse,
  GetTopItemsResponse,
  GetTopNotificationResponse,
  GetVerifyMintingResponse,
  PostVerifyMintingResponse,
  PredictMatchEventRequest,
  PredictMatchEventResponse,
  ReceiveLuckyDrawPrizeRequest,
  ReceiveLuckyDrawPrizeResponse,
  RegisterLuckyDrawCodeRequest,
  RegisterLuckyDrawCodeResponse,
  SavePointResponse,
  SetRepresentCardResponse,
  StartMiniGameResponse,
  UsePointResponse,
} from '@/store/server/common/football/football.dto';
import { footballService } from '@/store/server/common/football/football.service';
import type { OriginalsName } from '@/store/server/common/originals/originals.dto';
import { useGetOriginalsInitQuery } from '@/store/server/common/originals/originals.query';

export const USE_FOOTBALL_QUERY_KEY = {
  getRealTimeEdition: 'get-real-time-edition',
  getRealTimeCard: 'get-real-time-card',
  getBlockMiniGameInit: 'get-block-mini-game-init',
  getBlockMiniGameRanking: 'get-block-mini-game-ranking',
  getTopCollector: 'get-top-collector',
  getTopCollectorPeriod: 'get-top-collector-period',
  getTopNotification: 'get-top-notification',
  getPointSave: 'get-point-save',
  getPointUse: 'get-point-use',
  getPointUseDetail: 'get-point-use-detail',
  getMiniGameStatus: 'get-mini-game-status',
  getMiniGameRanking: 'get-mini-game-ranking',
  getRepresentCard: 'get-represent-card',
  getTopsItem: 'get-tops-item',
  getRepresentGallery: 'get-represent-gallery',
  getStickerList: 'get-sticker-list',
  getLuckyDrawTicketCount: 'get-lucky-draw-ticket-count',
  getLuckyDrawHistory: 'get-lucky-draw-history',
  getMatchEventHistory: 'get-match-event-history',
  getVerifyMinting: 'get-verify-minting',
};

// [메인] 실시간 카드 등록 현황_에디션 조회
interface GetRealTimeEditionParams {
  packIdx: number;
}

export const useGetRealTimeEditionQuery = ({ packIdx }: GetRealTimeEditionParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getRealTimeEdition, packIdx],
    queryFn: () => footballService.getRealTimeEdition({ pack_idx: packIdx }),
    select: (res: APICommonResponse<GetRealTimeEditionResponse>) => {
      const { data } = res.data;
      return {
        editionList: data.edition_list.map((item) => ({
          editionTitle: item.edition_title,
        })),
      };
    },
    enabled: !!packIdx,
  });
};

interface GetRealTimeEditionWithInitParams {
  originalsName: OriginalsName;
  packName: PackName;
}

export const useGetRealTimeEditionWithInitQuery = ({
  originalsName,
  packName,
}: GetRealTimeEditionWithInitParams) => {
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    error: initError,
  } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });

  const packIdx = initData?.[originalsName]?.packList.find((p) => p.packName === packName)?.packIdx ?? 0;

  const {
    data: realTimeEditionData,
    isLoading: isRealTimeEditionLoading,
    isError: isRealTimeEditionError,
    error: realTimeEditionError,
  } = useGetRealTimeEditionQuery({ packIdx });

  return {
    data: realTimeEditionData,
    isLoading: isRealTimeEditionLoading || isInitLoading,
    isError: isRealTimeEditionError || isInitError,
    error: realTimeEditionError || initError,
  };
};

// [메인] 실시간 카드 등록 현황
interface GetRealTimeCardParams {
  packIdx: number;
  editionTitle?: GetRealTimeCardRequest['edition_title'];
}

export const useGetRealTimeCardQuery = ({ packIdx, editionTitle }: GetRealTimeCardParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getRealTimeCard, packIdx, editionTitle],
    queryFn: () => footballService.getRealTimeCard({ pack_idx: packIdx, edition_title: editionTitle }),
    select: (res: APICommonResponse<GetRealTimeCardResponse>) => {
      const { data } = res.data;
      return {
        cardList: data.card_list.map((item) => ({
          ranking: item.ranking,
          cardName: item.card_name,
          ratio: item.ratio ?? 0.0,
        })),
      };
    },
    enabled: !!packIdx && !!editionTitle,
  });
};

interface GetRealTimeCardWithInitParams {
  originalsName: OriginalsName;
  packName: PackName;
  editionTitle?: GetRealTimeCardRequest['edition_title'];
}

export const useGetRealTimeCardWithInitQuery = ({
  originalsName,
  packName,
  editionTitle,
}: GetRealTimeCardWithInitParams) => {
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    isFetching: isInitFetching,
    error: initError,
  } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });

  const packIdx = initData?.[originalsName]?.packList.find((p) => p.packName === packName)?.packIdx ?? 0;

  const {
    data: realTimeCardData,
    isLoading: isRealTimeCardLoading,
    isError: isRealTimeCardError,
    isFetching: isRealTimeCardFetching,
    error: realTimeCardError,
  } = useGetRealTimeCardQuery({ packIdx, editionTitle });

  return {
    data: realTimeCardData,
    isLoading: isRealTimeCardLoading || isInitLoading,
    isError: isRealTimeCardError || isInitError,
    isFetching: isRealTimeCardFetching || isInitFetching,
    error: realTimeCardError || initError,
  };
};

// [블록 미니게임] 랭킹 조회
interface GetBlockMiniGameRankingParams {
  originalsIdx: number;
}

export const useGetBlockMiniGameRankingQuery = ({ originalsIdx }: GetBlockMiniGameRankingParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getBlockMiniGameRanking, originalsIdx],
    queryFn: () => footballService.getBlockMiniGameRanking({ originals_idx: originalsIdx }),
    select: (res: APICommonResponse<GetBlockMiniGameRankingResponse>) => {
      const { data } = res.data;
      return {
        my: data.my
          ? {
              ranking: data.my.ranking,
              profileImg: data.my.profile_img,
              nickName: data.my.nick_name,
              amount: data.my.amount,
            }
          : null,
        top: data.top
          ? data.top.map((item) => ({
              ranking: item.ranking,
              profileImg: item.profile_img,
              nickName: item.nick_name,
              amount: item.amount,
            }))
          : null,
      };
    },
    enabled: !!originalsIdx,
  });
};

interface GetBlockMiniGameRankingWithInitParams {
  originalsName: OriginalsName;
}

export const useGetBlockMiniGameRankingWithInitQuery = ({ originalsName }: GetBlockMiniGameRankingWithInitParams) => {
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    error: initError,
  } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });

  const originalsIdx = initData?.[originalsName]?.originalsIdx ?? 0;

  const {
    data: blockMiniGameRankingData,
    isLoading: isBlockMiniGameRankingLoading,
    isError: isBlockMiniGameRankingError,
    error: blockMiniGameRankingError,
  } = useGetBlockMiniGameRankingQuery({ originalsIdx });

  return {
    data: blockMiniGameRankingData,
    isLoading: isBlockMiniGameRankingLoading || isInitLoading,
    isError: isBlockMiniGameRankingError || isInitError,
    error: blockMiniGameRankingError || initError,
  };
};

// [블록 미니게임] 조회 (초기화)
interface GetBlockMiniGameInitParams {
  originalsIdx: number;
}

export const useGetBlockMiniGameInitQuery = ({ originalsIdx }: GetBlockMiniGameInitParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getBlockMiniGameInit, originalsIdx],
    queryFn: () => footballService.getBlockMiniGameInit({ originals_idx: originalsIdx }),
    select: (res: APICommonResponse<GetBlockMiniGameInitResponse>) => {
      const { data } = res.data;
      return {
        applyCount: data.apply_count,
      };
    },
    enabled: !!originalsIdx,
  });
};

// [블록 미니게임] 조회 (초기화) + originals init
interface GetBlockMiniGameInitWithInitParams {
  originalsName: OriginalsName;
}

export const useGetBlockMiniGameInitWithInitQuery = ({ originalsName }: GetBlockMiniGameInitWithInitParams) => {
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    error: initError,
  } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });

  const originalsIdx = initData?.[originalsName]?.originalsIdx ?? 0;

  const {
    data: blockMiniGameInitData,
    isLoading: isBlockMiniGameInitLoading,
    isError: isBlockMiniGameInitError,
    error: blockMiniGameInitError,
  } = useGetBlockMiniGameInitQuery({ originalsIdx });

  return {
    data: blockMiniGameInitData,
    originalsIdx,
    isLoading: isBlockMiniGameInitLoading || isInitLoading,
    isError: isBlockMiniGameInitError || isInitError,
    error: blockMiniGameInitError || initError,
  };
};

// [블록 미니게임] 종료
interface FinishBlockMiniGameParams {
  originalsIdx: number;
  amount: string;
}

export const useFinishBlockMiniGameMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<FinishBlockMiniGameResponse>) => void;
  onError?: (error: unknown) => void;
} = {}) => {
  return useMutation({
    mutationFn: (params: FinishBlockMiniGameParams) =>
      footballService.finishBlockMiniGame({ originals_idx: params.originalsIdx, amount: params.amount }),
    onSuccess,
    onError,
  });
};

// [탑 콜렉터] 조회
interface GetTopCollectorParams {
  packIdx: GetTopCollectorRequest['pack_idx'];
  season: GetTopCollectorRequest['season'];
}

export const useGetTopCollectorQuery = ({ packIdx, season }: GetTopCollectorParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getTopCollector, packIdx, season],
    queryFn: () => footballService.getTopCollector({ pack_idx: packIdx, season }),
    select: (res: APICommonResponse<GetTopCollectorResponse>) => {
      const { data } = res.data;
      return {
        my: data.my
          ? {
              nickName: data.my.nick_name,
              profileImg: data.my.profile_img,
              cardCnt: data.my.card_cnt,
              ranking: data.my.ranking,
            }
          : null,
        top: data.top
          ? data.top.map((item) => ({
              nickName: item.nick_name,
              profileImg: item.profile_img,
              cardCnt: item.card_cnt,
              ranking: item.ranking,
            }))
          : null,
      };
    },
    enabled: !!packIdx,
  });
};

interface GetTopCollectorWithInitParams {
  originalsName: OriginalsName;
  packName: PackName;
  season: GetTopCollectorParams['season'];
}

export const useGetTopCollectorWithInitQuery = ({
  originalsName,
  packName,
  season,
}: GetTopCollectorWithInitParams) => {
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    error: initError,
  } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });

  const packIdx = initData?.[originalsName]?.packList.find((p) => p.packName === packName)?.packIdx ?? 0;

  const {
    data: topCollectorData,
    isLoading: isTopCollectorLoading,
    isError: isTopCollectorError,
    error: topCollectorError,
  } = useGetTopCollectorQuery({ packIdx, season });

  return {
    data: topCollectorData,
    isLoading: isTopCollectorLoading || isInitLoading,
    isError: isTopCollectorError || isInitError,
    error: topCollectorError || initError,
  };
};

// [메인] 탑컬렉터 기간 조회
interface GetTopCollectorPeriodParams {
  packIdx: GetTopCollectorPeriodRequest['pack_idx'];
  season: GetTopCollectorPeriodRequest['season'];
}

export const useGetTopCollectorPeriodQuery = ({ packIdx, season }: GetTopCollectorPeriodParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getTopCollectorPeriod, packIdx, season],
    queryFn: () => footballService.getTopCollectorPeriod({ pack_idx: packIdx, season }),
    select: (res: APICommonResponse<GetTopCollectorPeriodResponse>) => {
      const { data } = res.data;
      return {
        startDateTime: data.start_dttm,
        endDateTime: data.end_dttm,
      };
    },
    enabled: !!packIdx,
  });
};

// [메인] 상단 알림 팝업 조회
interface GetTopNotificationParams {
  originalsIdx: number;
}

export const useGetTopNotificationQuery = ({ originalsIdx }: GetTopNotificationParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getTopNotification, originalsIdx],
    queryFn: () => footballService.getTopNotification({ originals_idx: originalsIdx }),
    select: (res: APICommonResponse<GetTopNotificationResponse>) => {
      const { data } = res.data;
      return {
        type: data.type,
        label: data.label,
      };
    },
    enabled: !!originalsIdx,
  });
};

interface GetTopNotificationWithInitParams {
  originalsName: OriginalsName;
}

export const useGetTopNotificationWithInitQuery = ({ originalsName }: GetTopNotificationWithInitParams) => {
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    error: initError,
  } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });

  const originalsIdx = initData?.[originalsName]?.originalsIdx ?? 0;

  const {
    data: topNotificationData,
    isLoading: isTopNotificationLoading,
    isError: isTopNotificationError,
    error: topNotificationError,
  } = useGetTopNotificationQuery({ originalsIdx });

  return {
    data: topNotificationData,
    isLoading: isTopNotificationLoading || isInitLoading,
    isError: isTopNotificationError || isInitError,
    error: topNotificationError || initError,
  };
};

// [포인트교환소] 포인트 모으기 조회
interface GetPointSaveParams {
  originalsIdx?: number;
  eventIdx?: number;
}

export const useGetPointSaveQuery = ({ originalsIdx, eventIdx }: GetPointSaveParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getPointSave, originalsIdx, eventIdx],
    queryFn: () => footballService.getPointSave({ originals_idx: originalsIdx, event_idx: eventIdx }),
    select: (res: APICommonResponse<GetPointSaveResponse>) => {
      const { data } = res.data;
      return {
        count: data.count,
        rows: data.rows.map((item) => ({
          methodIdx: item.method_idx,
          methodCode: item.method_code,
          title: item.title,
          image: item.image ?? '',
          point: item.point,
          link: item.link,
          applyCount: item.apply_count,
          offlineYn: item.offline_yn,
          startDttm: item.start_dttm,
          endDttm: item.end_dttm,
        })),
      };
    },
    enabled: !!originalsIdx,
  });
};

interface GetPointSaveWithInitParams {
  originalsName: OriginalsName;
}

export const useGetPointSaveWithInitQuery = ({ originalsName }: GetPointSaveWithInitParams) => {
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    error: initError,
  } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });

  const originalsIdx = initData?.[originalsName]?.originalsIdx ?? 0;

  const {
    data: pointSaveData,
    isLoading: isPointSaveLoading,
    isError: isPointSaveError,
    error: pointSaveError,
  } = useGetPointSaveQuery({ originalsIdx });

  return {
    data: pointSaveData,
    isLoading: isPointSaveLoading || isInitLoading,
    isError: isPointSaveError || isInitError,
    error: pointSaveError || initError,
  };
};

// [포인트교환소] 포인트 모으기 포인트받기
interface SavePointParams {
  methodCode: string;
  methodIdx: number;
}

export const useSavePointMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<SavePointResponse>) => void;
  onError?: (error: unknown) => void;
} = {}) => {
  return useMutation({
    mutationFn: (params: SavePointParams) =>
      footballService.savePoint({ method_code: params.methodCode, method_idx: params.methodIdx }),
    onSuccess,
    onError,
  });
};

// [포인트라운지] 오프라인 이벤트 참여
interface RegisterOfflineEventParams {
  methodIdx: number;
  methodCode: string;
}

export const useRegisterOfflineEventMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
} = {}) => {
  return useMutation({
    mutationFn: (params: RegisterOfflineEventParams) =>
      footballService.registerOfflineEvent({ method_idx: params.methodIdx, method_code: params.methodCode }),
    onSuccess,
    onError,
  });
};

// [포인트라운지] 포인트 사용하기 조회
interface GetPointUseParams {
  originalsIdx?: number;
  eventIdx?: number;
}

export const useGetPointUseQuery = ({ originalsIdx, eventIdx }: GetPointUseParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getPointUse, originalsIdx, eventIdx],
    queryFn: () => footballService.getPointUse({ originals_idx: originalsIdx, event_idx: eventIdx }),
    select: (res: APICommonResponse<GetPointUseResponse>) => {
      const { data } = res.data;
      return {
        count: data.count,
        rows: data.rows.map((item) => ({
          methodIdx: item.method_idx,
          methodCode: item.method_code,
          type: item?.type ?? 'exchange',
          title: item.title,
          image: item.image ?? '',
          point: item.point,
          remainAmount: item.remain_amount,
          startDttm: item.start_dttm,
          endDttm: item.end_dttm,
        })),
      };
    },
    enabled: !!originalsIdx,
  });
};

interface GetPointUseWithInitParams {
  originalsName: OriginalsName;
}

export const useGetPointUseWithInitQuery = ({ originalsName }: GetPointUseWithInitParams) => {
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    error: initError,
  } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });

  const originalsIdx = initData?.[originalsName]?.originalsIdx ?? 0;

  const {
    data: pointUseData,
    isLoading: isPointUseLoading,
    isError: isPointUseError,
    error: pointUseError,
  } = useGetPointUseQuery({ originalsIdx });

  return {
    data: pointUseData,
    isLoading: isPointUseLoading || isInitLoading,
    isError: isPointUseError || isInitError,
    error: pointUseError || initError,
  };
};

// [포인트라운지] 포인트 사용하기 상세조회
interface GetPointUseDetailParams {
  methodIdx: number;
}

export const useGetPointUseDetailQuery = ({ methodIdx }: GetPointUseDetailParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getPointUseDetail, methodIdx],
    queryFn: () => footballService.getPointUseDetail({ method_idx: methodIdx }),
    select: (res: APICommonResponse<GetPointUseDetailResponse>) => {
      const { data } = res.data;
      return {
        methodIdx: data.method_idx,
        methodCode: data.method_code,
        type: data.type,
        title: data.title,
        image: data.image,
        engTitle: data.eng_title,
        remainAmount: data.remain_amount,
        point: data.point,
        description: data.description,
        startDttm: data.start_dttm,
        endDttm: data.end_dttm,
        editionTitle: data.edition_title,
        holderAmount: data.holder_amount,
        applyYn: data.apply_yn,
        collectionInfo: {
          name: data.collection_info.name,
          phone: data.collection_info.phone,
          birth: data.collection_info.birth,
          address: data.collection_info.address,
          idCard: data.collection_info.id_card,
        },
        privacyUrl: data.privacy_url,
        thirdPartyUrl: data.third_party_url,
      };
    },
    enabled: !!methodIdx,
  });
};

// [포인트라운지] 포인트 사용하기 - 응모하기
interface UsePointParams {
  methodCode: string;
  methodIdx: number;
  amount: number;
  name?: string;
  phone?: string;
  birth?: string;
  postNo?: string;
  address?: string;
  addressDetail?: string;
  idCardFile?: File;
}

export const useUsePointMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<UsePointResponse>) => void;
  onError?: (error: unknown) => void;
} = {}) => {
  return useMutation({
    mutationFn: (params: UsePointParams) =>
      footballService.usePoint({
        method_code: params.methodCode,
        method_idx: params.methodIdx,
        amount: params.amount,
        name: params.name,
        phone: params.phone,
        birth: params.birth,
        post_no: params.postNo,
        address: params.address,
        address_detail: params.addressDetail,
        id_card_file: params.idCardFile,
      }),
    onSuccess,
    onError,
  });
};

// [미니게임] 미니게임 상태 조회
interface GetMiniGameStatusParams {
  originalsIdx: number;
}

export const useGetMiniGameStatusQuery = ({ originalsIdx }: GetMiniGameStatusParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getMiniGameStatus, originalsIdx],
    queryFn: () => footballService.getMiniGameStatus({ originals_idx: originalsIdx }),
    select: (res: APICommonResponse<GetMiniGameStatusResponse>) => {
      const { data } = res.data;
      return {
        participationAmount: data.participation_amount,
        nextAvailableDttm: data.next_available_dttm,
        startDttm: data.start_dttm,
        endDttm: data.end_dttm,
      };
    },
    enabled: !!originalsIdx,
  });
};

// [미니게임] 미니게임 시작
interface StartMiniGameParams {
  originalsIdx: number;
}

export const useStartMiniGameMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<StartMiniGameResponse>) => void;
  onError?: (error: unknown) => void;
} = {}) => {
  return useMutation({
    mutationFn: (params: StartMiniGameParams) => footballService.startMiniGame(params.originalsIdx),
    onSuccess,
    onError,
  });
};

// [미니게임] 미니게임 종료
interface FinishMiniGameParams {
  originalsIdx: number;
  amount: string;
}

export const useFinishMiniGameMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<FinishMiniGameResponse>) => void;
  onError?: (error: unknown) => void;
} = {}) => {
  return useMutation({
    mutationFn: (params: FinishMiniGameParams) =>
      footballService.finishMiniGame({ originals_idx: params.originalsIdx, amount: params.amount }),
    onSuccess,
    onError,
  });
};

// [미니게임] 미니게임 랭킹 조회
interface GetMiniGameRankingParams {
  originalsIdx: number;
}

export const useGetMiniGameRankingQuery = ({ originalsIdx }: GetMiniGameRankingParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getMiniGameRanking, originalsIdx],
    queryFn: () => footballService.getMiniGameRanking({ originals_idx: originalsIdx }),
    select: (res: APICommonResponse<GetMiniGameRankingResponse>) => {
      const { data } = res.data;
      return {
        my: data.my
          ? {
              ranking: data.my.ranking,
              profileImg: data.my.profile_img,
              nickName: data.my.nick_name,
              moveAmount: data.my.move_amount,
            }
          : null,
        top: data.top
          ? data.top.map((item) => ({
              ranking: item.ranking,
              profileImg: item.profile_img,
              nickName: item.nick_name,
              moveAmount: item.move_amount,
            }))
          : null,
      };
    },
    enabled: !!originalsIdx,
  });
};

interface GetMiniGameRankingWithInitParams {
  originalsName: OriginalsName;
}

export const useGetMiniGameRankingWithInitQuery = ({ originalsName }: GetMiniGameRankingWithInitParams) => {
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    error: initError,
  } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });

  const originalsIdx = initData?.[originalsName]?.originalsIdx ?? 0;

  const {
    data: miniGameRankingData,
    isLoading: isMiniGameRankingLoading,
    isError: isMiniGameRankingError,
    error: miniGameRankingError,
  } = useGetMiniGameRankingQuery({ originalsIdx });

  return {
    data: miniGameRankingData,
    isLoading: isMiniGameRankingLoading || isInitLoading,
    isError: isMiniGameRankingError || isInitError,
    error: miniGameRankingError || initError,
  };
};

// [메인] 대표 카드 조회
interface GetRepresentCardParams {
  originalsIdx: number;
}

export const useGetRepresentCardQuery = ({ originalsIdx }: GetRepresentCardParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getRepresentCard, originalsIdx],
    queryFn: () => footballService.getRepresentCard({ originals_idx: originalsIdx }),
    select: (res: APICommonResponse<GetRepresentCardResponse>) => {
      const { data } = res.data;
      if (Object.keys(data).length === 0) {
        return undefined;
      }

      return {
        metadataIdx: data.metadata_idx,
        cardName: data.card_name,
        image: data.image,
        width: data.width,
        height: data.height,
        stickers: data.stickers.map((sticker) => ({
          idx: sticker.idx,
          src: sticker.src,
          x: sticker.x,
          y: sticker.y,
          width: sticker.width,
          height: sticker.height,
          rotation: sticker.rotation,
        })),
      };
    },
    enabled: !!originalsIdx,
  });
};

interface GetRepresentCardWithInitParams {
  originalsName: OriginalsName;
}

export const useGetRepresentCardWithInitQuery = ({ originalsName }: GetRepresentCardWithInitParams) => {
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    error: initError,
  } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });

  const originalsIdx = initData?.[originalsName]?.originalsIdx ?? 0;

  const {
    data: representCardData,
    isLoading: isRepresentCardLoading,
    isError: isRepresentCardError,
    error: representCardError,
  } = useGetRepresentCardQuery({ originalsIdx });

  return {
    data: representCardData,
    isLoading: isRepresentCardLoading || isInitLoading,
    isError: isRepresentCardError || isInitError,
    error: representCardError || initError,
  };
};

// [메인] 대표 카드 설정
interface SetRepresentCardParams {
  originalsIdx: number;
  metadataIdx: number;
  width: number;
  height: number;
  stickers: { idx: string; src: string; x: number; y: number; width: number; height: number; rotation: number }[];
}

export const useSetRepresentCardMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<SetRepresentCardResponse>) => void;
  onError?: (error: unknown) => void;
} = {}) => {
  return useMutation({
    mutationFn: (params: SetRepresentCardParams) =>
      footballService.setRepresentCard({
        originals_idx: params.originalsIdx,
        metadata_idx: params.metadataIdx,
        width: params.width,
        height: params.height,
        stickers: params.stickers,
      }),
    onSuccess,
    onError,
  });
};

// [메인] 인기 상품 조회
interface GetTopItemsParams {
  originalsIdx: number;
}

export const useGetTopItemsQuery = ({ originalsIdx }: GetTopItemsParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getTopsItem, originalsIdx],
    queryFn: () => footballService.getTopItems({ originals_idx: originalsIdx }),
    select: (res: APICommonResponse<GetTopItemsResponse>) => {
      const { data } = res.data;
      return (data.list ?? []).map((item) => ({
        methodIdx: item.method_idx,
        title: item.title,
        point: item.point,
        image: item.image,
        rank: item.rank,
      }));
    },
    enabled: !!originalsIdx,
  });
};

interface GetTopItemsWithInitParams {
  originalsName: OriginalsName;
}

export const useGetTopItemsWithInitQuery = ({ originalsName }: GetTopItemsWithInitParams) => {
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    error: initError,
  } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });

  const originalsIdx = initData?.[originalsName]?.originalsIdx ?? 0;

  const {
    data: topItemsData,
    isLoading: isTopItemsLoading,
    isError: isTopItemsError,
    error: topItemsError,
  } = useGetTopItemsQuery({ originalsIdx });

  return {
    data: topItemsData,
    isLoading: isTopItemsLoading || isInitLoading,
    isError: isTopItemsError || isInitError,
    error: topItemsError || initError,
  };
};

// [메인] 대표 갤러리 조회
interface GetRepresentGalleryParams {
  originalsIdx: number;
}

export const useGetRepresentGalleryQuery = ({ originalsIdx }: GetRepresentGalleryParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getRepresentGallery, originalsIdx],
    queryFn: () => footballService.getRepresentGallery({ originals_idx: originalsIdx }),
    select: (res: APICommonResponse<GetRepresentGalleryResponse>) => {
      const { data } = res.data;
      return (data.represent_list ?? []).map((item) => ({
        nickName: item.nick_name,
        profileImage: item.profile_img,
        metadataIdx: item.metadata_idx,
        cardName: item.card_name,
        image: item.image,
        width: item.width,
        height: item.height,
        stickers: item.stickers.map((sticker) => ({
          idx: sticker.idx,
          src: sticker.src,
          x: sticker.x,
          y: sticker.y,
          width: sticker.width,
          height: sticker.height,
          rotation: sticker.rotation,
        })),
      }));
    },
    enabled: !!originalsIdx,
  });
};

interface GetRepresentGalleryWithInitParams {
  originalsName: OriginalsName;
}

export const useGetRepresentGalleryWithInitQuery = ({ originalsName }: GetRepresentGalleryWithInitParams) => {
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    error: initError,
  } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });

  const originalsIdx = initData?.[originalsName]?.originalsIdx ?? 0;

  const {
    data: representGalleryData,
    isLoading: isRepresentGalleryLoading,
    isError: isRepresentGalleryError,
    error: representGalleryError,
  } = useGetRepresentGalleryQuery({ originalsIdx });

  return {
    data: representGalleryData,
    isLoading: isRepresentGalleryLoading || isInitLoading,
    isError: isRepresentGalleryError || isInitError,
    error: representGalleryError || initError,
  };
};

/**
 * ---------- 스티커 관련 Query Hook ----------
 */
interface GetStickerListParams {
  originalsIdx: number;
}

export const useGetStickerListQuery = ({ originalsIdx }: GetStickerListParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getStickerList, originalsIdx],
    queryFn: () => footballService.getStickerList({ originals_idx: originalsIdx }),
    select: (res: APICommonResponse<GetStickerListResponse>) => {
      const { data } = res.data;
      return data.map((item) => ({
        category: item.category,
        stickerIdx: item.sticker_idx,
        src: item.src,
        point: item.point,
        useYN: item.use_yn,
      }));
    },
    enabled: !!originalsIdx,
  });
};

interface GetStickerListWithInitParams {
  originalsName: OriginalsName;
}

export const useGetStickerListWithInitQuery = ({ originalsName }: GetStickerListWithInitParams) => {
  const {
    data: initData,
    isLoading: isInitLoading,
    isError: isInitError,
    error: initError,
  } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });

  const originalsIdx = initData?.[originalsName]?.originalsIdx ?? 0;

  const {
    data: stickerListData,
    isLoading: isStickerListLoading,
    isError: isStickerListError,
    error: stickerListError,
  } = useGetStickerListQuery({ originalsIdx });

  return {
    data: stickerListData,
    isLoading: isStickerListLoading || isInitLoading,
    isError: isStickerListError || isInitError,
    error: stickerListError || initError,
  };
};

// [스티커] 스티커 구매
interface BuyStickerParams {
  originalsIdx: number;
  stickerIdx: string;
}

export const useBuyStickerMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<BuyStickerResponse>) => void;
  onError?: (error: unknown) => void;
} = {}) => {
  return useMutation({
    mutationFn: (params: BuyStickerParams) =>
      footballService.buySticker({
        originals_idx: params.originalsIdx,
        sticker_idx: params.stickerIdx,
      }),
    onSuccess,
    onError,
  });
};

/**
 * ---------- 팝업 관련 Query Hook ----------
 */

// [럭키드로우] 코드 등록
export const useRegisterLuckyDrawCodeMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<RegisterLuckyDrawCodeResponse>) => void;
  onError?: (error: unknown) => void;
} = {}) => {
  return useMutation({
    mutationFn: (data: RegisterLuckyDrawCodeRequest) => footballService.registerLuckyDrawCode(data),
    onSuccess,
    onError,
  });
};

// [럭키드로우] 보유 응모권 조회
export const useGetLuckyDrawTicketCountQuery = () => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getLuckyDrawTicketCount],
    queryFn: () => footballService.getLuckyDrawTicketCount(),
    select: (res: APICommonResponse<GetLuckyDrawTicketCountResponse>) => res.data.data,
    retry: false,
  });
};

// [럭키드로우] 당첨 내역 조회
export const useGetLuckyDrawHistoryQuery = () => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getLuckyDrawHistory],
    queryFn: () => footballService.getLuckyDrawHistory(),
    select: (res: APICommonResponse<GetLuckyDrawHistoryResponse>) => {
      const { data } = res.data;
      return {
        applyHistory: data.apply_hist.map((item) => ({
          code: item.code,
          product: item.product,
          completeYN: item.complete_yn,
        })),
      };
    },
  });
};

// [럭키드로우] 수령하기
export const useReceiveLuckyDrawPrizeMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<ReceiveLuckyDrawPrizeResponse>) => void;
  onError?: (error: unknown) => void;
} = {}) => {
  return useMutation({
    mutationFn: (data: ReceiveLuckyDrawPrizeRequest) => footballService.receiveLuckyDrawPrize(data),
    onSuccess,
    onError,
  });
};

// [럭키드로우] 카드 뽑기
export const useDrawLuckyDrawMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<DrawLuckyDrawResponse>) => void;
  onError?: (error: unknown) => void;
} = {}) => {
  return useMutation({
    mutationFn: () => footballService.drawLuckyDraw(),
    onSuccess,
    onError,
  });
};

// [예측 이벤트] 제출 여부 조회
export const useGetMatchEventHistoryQuery = () => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getMatchEventHistory],
    queryFn: () => footballService.getMatchEventHistory(),
    select: (res: APICommonResponse<GetMatchEventHistoryResponse>) => res.data.data,
  });
};

// [예측 이벤트] 예측하기
export const usePredictMatchEventMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<PredictMatchEventResponse>) => void;
  onError?: (error: unknown) => void;
} = {}) => {
  return useMutation({
    mutationFn: (data: PredictMatchEventRequest) => footballService.predictMatchEvent(data),
    onSuccess,
    onError,
  });
};

// [내컬렉션] 엠블럼카드 수령 여부 조회
interface GetVerifyMintingParams {
  packIdx: number;
}

export const useGetVerifyMintingQuery = ({ packIdx }: GetVerifyMintingParams) => {
  return useQuery({
    queryKey: [USE_FOOTBALL_QUERY_KEY.getVerifyMinting, packIdx],
    queryFn: () => footballService.getVerifyMinting({ pack_idx: packIdx, card_name: '2026 엠블럼' }),
    select: (res: APICommonResponse<GetVerifyMintingResponse>) => {
      const { data } = res.data;

      return {
        metadataIdx: data.metadata_idx,
        receiveYn: data.receive_yn,
      };
    },
    enabled: !!packIdx,
  });
};

// [내컬렉션] 엠블럼카드 받기
interface PostVerifyMintingParams {
  packIdx: number;
  metadataIdx: number;
}

export const usePostVerifyMintingMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: APICommonResponse<PostVerifyMintingResponse>) => void;
  onError?: (error: unknown) => void;
} = {}) => {
  return useMutation({
    mutationFn: (params: PostVerifyMintingParams) =>
      footballService.postVerifyMinting({ pack_idx: params.packIdx, metadata_idx: params.metadataIdx }),
    onSuccess,
    onError,
  });
};
